import React, { useState } from 'react';
import { IconButton, styled, keyframes, Box, Menu, MenuItem, ListItemText } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import SettingsIcon from '@mui/icons-material/Settings';

const ripple = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.3), 0 0 0 4px rgba(25, 118, 210, 0.1);
  }
  50% {
    box-shadow: 0 0 0 5px rgba(25, 118, 210, 0.2), 0 0 0 9px rgba(25, 118, 210, 0.07);
  }
  100% {
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0), 0 0 0 14px rgba(25, 118, 210, 0);
  }
`;

const waveVibrate = keyframes`
  0% {
    transform: scaleY(0.8);
  }
  30% {
    transform: scaleY(1.2);
  }
  60% {
    transform: scaleY(0.7);
  }
  80% {
    transform: scaleY(1.15);
  }
  100% {
    transform: scaleY(0.8);
  }
`;

const AnimatedIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isPlaying',
})(({ isPlaying }) => ({
  color: isPlaying ? '#1976d2' : '#666',
  backgroundColor: '#f5f5f5',
  flexShrink: 0,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#e0e0e0'
  },
  '&:disabled': {
    backgroundColor: '#f5f5f5',
    color: '#bdbdbd'
  },
  ...(isPlaying && {
    color: '#1976d2',
    backgroundColor: '#e3f2fd',
    animation: `${ripple} 2.4s infinite ease-in-out`,
    '&:hover': {
      backgroundColor: '#bbdefb'
    },
    '& svg': {
      animation: `${waveVibrate} 1.6s infinite ease-in-out`,
      transformOrigin: 'center'
    }
  }),
}));

const TTSButton = ({
  isPlaying,
  isDisabled,
  onToggle,
  audioOutputDevices = [],
  selectedOutputDeviceId = 'default',
  onOutputDeviceChange,
  onRestart,
  // legacy voice props kept for backward compat but not used in menu
  availableVoices,
  selectedVoiceName,
  onVoiceChange
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeviceSelect = (deviceId) => {
    if (onOutputDeviceChange) onOutputDeviceChange(deviceId);
    handleMenuClose();
    // Restart reading on the newly selected device after the browser has
    // finished processing the cancel() call from setOutputDevice.
    if (isPlaying && onRestart) {
      setTimeout(onRestart, 350);
    }
  };

  const getDeviceLabel = (device, index) => {
    if (device.label) return device.label;
    if (device.deviceId === 'default') return 'Default Speaker';
    if (device.deviceId === 'communications') return 'Communications Speaker';
    return `Speaker ${index + 1}`;
  };

  const getIcon = () => {
    if (isDisabled) {
      return <VolumeOffIcon />;
    }
    if (isPlaying) {
      return <GraphicEqIcon />;
    }
    return <VolumeUpIcon />;
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
      <AnimatedIconButton
        onClick={onToggle}
        disabled={isDisabled}
        isPlaying={isPlaying}
        size="small"
        role="button"
        aria-pressed={isPlaying}
        aria-label="Text to speech"
      >
        {getIcon()}
      </AnimatedIconButton>

      {isPlaying && (
        <>
          <IconButton
            onClick={handleMenuOpen}
            size="small"
            sx={{
              color: '#1976d2',
              backgroundColor: '#f5f5f5',
              width: 30,
              height: 30,
              flexShrink: 0,
              border: '1px solid rgba(25, 118, 210, 0.1)',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              }
            }}
            title="Choose Speaker / Voice"
          >
            <SettingsIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                maxHeight: 280,
                width: '240px',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
                border: '1px solid rgba(0,0,0,0.06)'
              }
            }}
          >
            <MenuItem disabled sx={{ fontWeight: 700, fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Output Speaker
            </MenuItem>
            {audioOutputDevices.length === 0 ? (
              <MenuItem disabled sx={{ fontSize: '12px', py: 1, color: '#888' }}>
                No devices found — using system default
              </MenuItem>
            ) : (
              audioOutputDevices.map((device, idx) => {
                const label = getDeviceLabel(device, idx);
                const isSelected = device.deviceId === selectedOutputDeviceId;
                return (
                  <MenuItem
                    key={device.deviceId}
                    selected={isSelected}
                    onClick={() => handleDeviceSelect(device.deviceId)}
                    sx={{ fontSize: '12px', py: 0.75 }}
                  >
                    <ListItemText
                      primary={label}
                      primaryTypographyProps={{ fontSize: '13px', fontWeight: isSelected ? 700 : 500 }}
                    />
                  </MenuItem>
                );
              })
            )}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default TTSButton;
